import { Lock, ArrowDown, AlertTriangle, LinkIcon, Smartphone, LayoutGrid, Phone, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { getCommuneBySlug, type CommuneData } from '@/data/communes';

const IMG_ARTISAN = '/images/commune-artisan-template.webp';

const IMG_MECANISME = '/images/commune-mecanisme-template.webp';

const IMG_INTERVENTION = '/images/commune-intervention-template.webp';

interface PanneItem {
  id: string;
  icon: LucideIcon;
  title: string;
  desc: string;
}

function buildPannes(nom: string): PanneItem[] {
  return [
    { id: 'bloque', icon: Lock, title: 'Volet bloqué', desc: `Votre volet est complètement bloqué, impossible de le bouger ? Nous diagnostiquons la cause (moteur, axe, tablier, corps étranger) et débloquons votre volet à ${nom}.` },
    { id: 'remonte-pas', icon: ArrowDown, title: 'Volet qui ne remonte plus', desc: `Le volet descend mais refuse de remonter ? Problème de fin de course, condensateur ou enrouleur. Nous réparons ce type de panne fréquent à ${nom} en une intervention.` },
    { id: 'moteur-hs', icon: AlertTriangle, title: 'Moteur HS', desc: `Le moteur ne répond plus ou fait un bruit anormal ? Nous remplaçons les moteurs tubulaires toutes marques à ${nom} : Somfy, Simu, Nice, Bubendorff, Profalux.` },
    { id: 'sangle', icon: LinkIcon, title: 'Sangle ou manivelle cassée', desc: `Sangle déchirée, effilochée ou mécanisme de manivelle cassé ? Nous remplaçons sangle, enrouleur et manivelle à ${nom}. Possibilité de passer à la motorisation.` },
    { id: 'telecommande', icon: Smartphone, title: 'Télécommande HS', desc: `La télécommande ne pilote plus le volet ? Nous diagnostiquons et reprogrammons votre télécommande ou la remplaçons à ${nom}. Compatible Somfy, Simu, Nice.` },
    { id: 'lames', icon: LayoutGrid, title: 'Lames ou tablier abîmés', desc: `Des lames tordues, cassées ou un tablier endommagé ? Nous remplaçons les éléments défectueux à ${nom} pour retrouver un volet comme neuf.` },
  ];
}

function getIntroParagraphs(commune: CommuneData): string[] {
  const { nom, codePostal } = commune;
  const idx = nom.length % 4;

  const p1Options = [
    `Vous habitez à <strong>${nom}</strong>${codePostal ? ` (${codePostal})` : ''} et votre volet roulant est bloqué, ne remonte plus, ou fait un bruit anormal ? Nous intervenons rapidement pour diagnostiquer et réparer votre volet roulant, quelle que soit la panne.`,
    `Votre volet roulant est en panne à <strong>${nom}</strong> ? Qu'il soit manuel ou électrique, bloqué ou cassé, notre équipe se déplace chez vous pour un diagnostic complet et une réparation efficace.`,
    `Un volet roulant qui coince, un moteur qui ne répond plus, une sangle qui lâche : à <strong>${nom}</strong>, ces pannes n'attendent pas. Nous intervenons rapidement pour sécuriser votre habitation et remettre votre volet en état de marche.`,
    `À <strong>${nom}</strong>, votre volet roulant mérite l'attention d'un vrai spécialiste. Nous ne remplaçons pas systématiquement : nous diagnostiquons, nous réparons, et nous vous garantissons un travail soigné et durable.`,
  ];

  const p2Options = [
    `Basés à <strong>Charleroi</strong>, nous nous déplaçons dans tout le <strong>Hainaut</strong>, y compris à ${nom} et ses environs. Notre priorité : <strong>réparer plutôt que remplacer</strong>, pour vous offrir la solution la plus économique et durable.`,
    `Spécialistes de la <strong>réparation de volets roulants</strong>, nous intervenons à ${nom} avec tout l'équipement nécessaire. Diagnostic gratuit, devis transparent, et intervention soignée : c'est notre engagement.`,
    `De la simple sangle cassée au moteur tubulaire à remplacer, nous prenons en charge <strong>tous les types de pannes</strong> à ${nom}. Nous travaillons sur toutes les marques : Somfy, Simu, Nice, Bubendorff, Profalux et bien d'autres.`,
    `Avec plus de <strong>10 ans de garantie</strong> sur nos interventions, vous êtes tranquille. Notre équipe connaît parfaitement le secteur de ${nom} et intervient dans les meilleurs délais.`,
  ];

  return [p1Options[idx], p2Options[(idx + 1) % 4]];
}

function formatVoisinName(slug: string): string {
  return slug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

export function ContentCommune({ commune }: { commune: CommuneData }) {
  const { nom, codePostal, communesVoisines } = commune;
  const intros = getIntroParagraphs(commune);
  const pannes = buildPannes(nom);

  return (
    <>
      {/* Intro concrète */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="fade-up max-w-4xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-700 mb-5">
                Votre volet roulant en panne à {nom} ? On s'en occupe.
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                {intros.map((p, i) => (
                  <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
                ))}
                <p>
                  Que votre volet soit <strong>manuel</strong> (sangle ou manivelle) ou <strong>électrique</strong> (motorisé avec télécommande), nous avons l'expertise et les pièces nécessaires pour le remettre en état. Toutes nos interventions sont couvertes par une <strong>garantie de 10 ans</strong>.
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden h-72 lg:h-80">
              <img
                src={IMG_ARTISAN}
                alt={`Intervention réparation volet roulant à ${nom} — artisan spécialiste`}
                title={`Réparation volet roulant ${nom}`}
                className="w-full h-full object-cover object-top"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Ce que nous réparons */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="fade-up max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="section-title text-center mb-4">Ce que nous réparons à {nom}</h2>
          <p className="section-subtitle text-center mb-10">
            Tous les types de pannes sur volets roulants, manuels ou électriques
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {pannes.map((panne) => {
              const Icon = panne.icon;
              return (
                <div key={panne.id} className="card border border-background-200">
                  <div className="w-11 h-11 rounded-xl bg-accent-50 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-accent-500" />
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-700 text-sm mb-2">{panne.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{panne.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Images d'illustration */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-10">
            <div className="rounded-2xl overflow-hidden h-56 md:h-64">
              <img
                src={IMG_MECANISME}
                alt={`Mécanisme interne de volet roulant — réparation à ${nom}`}
                title={`Moteur et mécanisme volet roulant ${nom}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="rounded-2xl overflow-hidden h-56 md:h-64">
              <img
                src={IMG_INTERVENTION}
                alt={`Intervention à domicile pour réparation de volet roulant à ${nom}`}
                title={`Dépannage volet roulant à domicile ${nom}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Intervention à [Nom] et alentours */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="fade-up max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-700 text-center mb-6">
            Intervention à {nom} et alentours
          </h2>
          <div className="card border border-background-200 p-6 md:p-8">
            <div className="text-center mb-6">
              {codePostal && (
                <p className="text-lg font-heading font-semibold text-foreground-700 mb-1">
                  {nom} ({codePostal})
                </p>
              )}
              <p className="text-sm text-foreground-500">
                Nous nous déplaçons à {nom} et dans les communes voisines pour toute intervention sur volet roulant.
              </p>
            </div>

            {communesVoisines.length > 0 && (
              <div className="mb-6">
                <p className="text-sm font-medium text-foreground-600 mb-3 text-center">
                  Communes voisines également couvertes :
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {communesVoisines.map((voisinSlug) => {
                    const v = getCommuneBySlug(voisinSlug);
                    if (!v) return null;
                    return (
                      <Link
                        key={voisinSlug}
                        to={v.urlPath}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-50 border border-accent-100 rounded-lg text-sm text-accent-600 hover:bg-accent-100 transition-colors whitespace-nowrap"
                      >
                        {v.nom}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4 border-t border-background-200">
              <a
                href="tel:+3280020410"
                className="btn-primary text-sm px-6 py-2.5 inline-flex items-center gap-2 whitespace-nowrap"
              >
                <Phone className="w-4 h-4" />
                0800 20 410
              </a>
              <a
                href="https://wa.me/32475585219"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline text-sm px-6 py-2.5 inline-flex items-center gap-2 whitespace-nowrap"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Bloc motorisation */}
      <section className="py-10 md:py-14 bg-primary-500">
        <div className="fade-up max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-white mb-3">
            Motorisez vos volets à {nom}
          </h2>
          <p className="text-sm md:text-base text-white/80 mb-5">
            Transformez votre volet manuel en volet électrique. Installation de moteur tubulaire, télécommande et pilotage connecté.
          </p>
          <Link
            to="/motorisation-volet-charleroi/"
            className="btn-secondary text-sm px-6 py-2.5 inline-flex items-center gap-2 border-white/30 hover:bg-white/10 whitespace-nowrap"
          >
            En savoir plus sur la motorisation
          </Link>
        </div>
      </section>
    </>
  );
}